import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Loader2, Lock, Mail, User } from 'lucide-react';
import { api } from '../lib/api';
import type { AuthUser } from '../types/auth';
// Single source of truth for handle rules, shared with the server (apps/api).
import { validateUsername, sanitizeUsernameInput } from '@echo/shared';

function BrandHeader() {
  return (
    <div className="mb-4 flex flex-col items-center gap-2 select-none">
      <img src="/brand/Echo_logo_plant_sm.png" alt="Echo" width="104" height="168" className="h-14 w-auto object-contain" fetchPriority="high" />
      <div className="text-lg tracking-wide font-medium text-emerald-300">Echo</div>
    </div>
  );
}

/** An underlined field with a floating label and a trailing icon; the label rises once the field is focused or filled. */
function AuthField({ icon, label, ...input }: { icon: ReactNode; label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="relative">
      <input
        {...input}
        aria-label={label}
        placeholder={label}
        className="peer w-full border-0 border-b border-white/30 bg-transparent pb-1.5 pr-7 pt-5 text-sm text-fg placeholder-transparent outline-none transition-colors focus:border-emerald-300"
      />
      <label className="pointer-events-none absolute left-0 top-5 origin-left text-sm text-fg-2 transition-all peer-focus:top-0 peer-focus:text-xs peer-focus:text-emerald-300 peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:text-xs">
        {label}
      </label>
      <span aria-hidden className="absolute right-0 top-6 text-fg-2 transition-colors peer-focus:text-emerald-300">{icon}</span>
    </div>
  );
}

type Mode = 'login' | 'signup' | 'pending_verify' | 'resent' | 'forgot' | 'forgot_sent';

// HTML autocomplete hints for the password field (browser/password-manager cues, not credentials).
const AUTOFILL_HINT = { signup: 'new-password', login: 'current-password' } as const;

export default function Login({ onAuth }: { onAuth: (token: string, user: AuthUser, isFirstLogin?: boolean) => void }) {
  const [mode, setMode] = useState<Mode>('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  // Which way the panel last swept; null until the user switches views (the first paint doesn't animate).
  const [sweep, setSweep] = useState<'to-signup' | 'to-login' | null>(null);
  const [leaving, setLeaving] = useState(false);
  const swapTimer = useRef<number | null>(null);
  useEffect(() => () => { if (swapTimer.current !== null) window.clearTimeout(swapTimer.current); }, []);

  const buttonLabel = useMemo(() => {
    if (busy) return mode === 'login' ? 'Signing in…' : 'Creating account…';
    return mode === 'login' ? 'Login' : 'Sign Up';
  }, [mode, busy]);

  // Live handle feedback (signup only): null while empty, otherwise an error
  // string or null when valid.
  const usernameError = useMemo(
    () => (mode === 'signup' && username ? validateUsername(username) : null),
    [mode, username]
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    setMsg('');
    setBusy(true);
    // Carry any pending invite so the server adds the user to the space the moment
    // their account authenticates — independent of the post-login client redeem.
    let inviteCode: string | undefined;
    try { inviteCode = localStorage.getItem('__pending_invite_code') || undefined; } catch { /* noop */ }
    try {
      if (mode === 'signup') {
        const ue = validateUsername(username);
        if (ue) { setErr(ue); setBusy(false); return; }
        if (password !== confirmPassword) { setErr('Passwords do not match'); setBusy(false); return; }
        await api.post('/auth/signup', { username, email, password, inviteCode });
        setResendEmail(email);
        setMode('pending_verify');
      } else {
        const res = await api.post('/auth/login', { username, password, inviteCode });
        // The server redeemed any invite during login; clear the pending code so a
        // later normal login can't silently re-add the user to that space.
        try { localStorage.removeItem('__pending_invite_code'); } catch { /* noop */ }
        onAuth(res.token, res.user as AuthUser, !!res.isFirstLogin);
      }
    } catch (error: unknown) {
      const e = error as any;
      const message: string = e?.message || 'Auth failed';
      if (message.toLowerCase().includes('email not verified')) {
        // Try to surface the email from the username lookup so the user can resend
        setResendEmail('');
        setErr('');
        setMode('pending_verify');
      } else {
        setErr(message);
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleResend() {
    if (!resendEmail) { setErr('Enter your email address below to resend.'); return; }
    setBusy(true);
    setErr('');
    try {
      await api.post('/auth/resend-verification', { email: resendEmail });
      setMode('resent');
    } catch {
      setErr('Failed to resend. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  // ── Forgot password screen ─────────────────────────────────────────────────
  if (mode === 'forgot' || mode === 'forgot_sent') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-fg brand-login-bg p-6">
        <BrandHeader />
        <div className="bg-white/[0.04] border border-white/10 p-6 rounded-2xl w-full max-w-xs space-y-4">
          {mode === 'forgot_sent' ? (
            <>
              <div className="flex justify-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7 text-emerald-400"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                </span>
              </div>
              <div className="text-center space-y-1">
                <h2 className="text-lg font-medium text-white">Check your email</h2>
                <p className="text-sm text-fg-2">If that email is registered, a reset link has been sent. Check your inbox and spam folder.</p>
              </div>
            </>
          ) : (
            <>
              <div>
                <h2 className="text-xl font-medium text-fg text-center">Reset password</h2>
                <p className="text-sm text-fg-2 text-center mt-1">Enter your email and we'll send a reset link.</p>
              </div>
              {err && <div role="alert" className="flex items-start gap-2 rounded-md border-l-2 border-red-500 bg-red-500/10 px-3 py-2 text-sm text-red-300">{err}</div>}
              <input
                className="w-full p-2.5 rounded-md bg-neutral-900 text-fg placeholder-neutral-500 border border-neutral-800 focus:outline-none focus:ring-2 focus:ring-emerald-600/60"
                type="email"
                placeholder="Your email address"
                aria-label="Your email address"
                autoComplete="email"
                value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
              />
              <button
                disabled={busy || !forgotEmail}
                onClick={async () => {
                  setBusy(true); setErr('');
                  try {
                    await api.post('/auth/forgot-password', { email: forgotEmail });
                    setMode('forgot_sent');
                  } catch (e: any) {
                    setErr(e?.message || 'Something went wrong. Please try again.');
                  } finally { setBusy(false); }
                }}
                className="w-full py-2.5 rounded-md text-white bg-brand hover:brightness-110 transition-colors disabled:opacity-60"
              >
                {busy ? 'Sending…' : 'Send reset link'}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => { setMode('login'); setErr(''); }}
            className="w-full text-center text-sm text-fg-2 hover:text-fg underline"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  // ── Check your email screen ────────────────────────────────────────────────
  if (mode === 'pending_verify' || mode === 'resent') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-fg brand-login-bg p-6">
        <BrandHeader />
        <div className="bg-white/[0.04] border border-white/10 p-6 rounded-2xl w-full max-w-xs space-y-4 text-center">
          <div className="flex justify-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7 text-emerald-400">
                <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
            </span>
          </div>
          <h2 className="text-lg font-medium text-white">
            {mode === 'resent' ? 'Email resent!' : 'Check your email'}
          </h2>
          <p className="text-sm text-fg-2">
            {mode === 'resent'
              ? `A new verification link has been sent${resendEmail ? ` to ${resendEmail}` : ''}. Check your inbox and spam folder.`
              : `We sent a verification link${resendEmail ? ` to ${resendEmail}` : ' to your email address'}. Click it to activate your account.`}
          </p>
          {err && <div role="alert" className="flex items-start gap-2 rounded-md border-l-2 border-red-500 bg-red-500/10 px-3 py-2 text-sm text-red-300">{err}</div>}
          {mode === 'pending_verify' && (
            <div className="space-y-2 pt-1">
              {!resendEmail && (
                <input
                  className="w-full p-2.5 rounded-md bg-neutral-900 text-fg placeholder-neutral-500 border border-neutral-800 focus:outline-none focus:ring-2 focus:ring-emerald-600/60 text-sm"
                  placeholder="Your email address"
                  aria-label="Your email address"
                  type="email"
                  value={resendEmail}
                  onChange={e => setResendEmail(e.target.value)}
                />
              )}
              <button
                disabled={busy}
                onClick={handleResend}
                className="w-full py-2 rounded-md text-sm text-emerald-300 border border-emerald-800/60 hover:bg-emerald-900/30 disabled:opacity-50 transition-colors"
              >
                {busy ? 'Sending…' : 'Resend verification email'}
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={() => { setMode('login'); setErr(''); setMsg(''); }}
            className="text-sm text-fg-2 hover:text-fg underline"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  // ── Login / Signup form ────────────────────────────────────────────────────
  const isSignup = mode === 'signup';
  // Switching views: the panel starts sweeping and the old content fades out; once the panel covers the card (about
  // 42% into the 1s sweep) the view swaps underneath it, so the card's height change and the new fields are hidden,
  // and the new content rises in as the panel pulls away.
  const SWEEP_SWAP_MS = 420;
  const switchTo = (next: 'login' | 'signup') => {
    if (next === mode || leaving) return;
    setErr('');
    setMsg('');
    const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) { setMode(next); return; }
    setSweep(next === 'signup' ? 'to-signup' : 'to-login');
    setLeaving(true);
    swapTimer.current = window.setTimeout(() => { swapTimer.current = null; setMode(next); setLeaving(false); }, SWEEP_SWAP_MS);
  };
  const swept = sweep ? 'auth-swept' : '';
  const leave = leaving ? 'auth-leaving' : '';

  return (
    <div className="flex flex-col items-center justify-center min-h-screen text-fg brand-login-bg p-4 sm:p-6">
      <form onSubmit={submit} className="auth-card w-full max-w-sm md:max-w-3xl md:min-h-[27rem]">
        {/* The sweeping panel and its welcome text */}
        <div aria-hidden className="auth-panel" data-shape={isSignup ? 'signup' : 'login'} data-sweep={sweep ?? undefined} />
        <div
          key={mode}
          className={`${swept} ${leave} pointer-events-none absolute inset-x-0 top-0 flex h-[5.25rem] items-center px-6 select-none md:inset-y-0 md:h-auto md:w-[34%] md:items-start md:justify-center md:px-0 md:pt-12 ${
            isSignup ? 'md:left-0 md:right-auto' : 'md:left-auto md:right-0'
          }`}
        >
          <img src="/brand/Echo_logo_plant_sm.png" alt="Echo" width="104" height="168" className="h-12 w-auto shrink-0 object-contain brightness-0 invert opacity-90 md:h-28" />
        </div>

        {/* The form */}
        <div key={`form-${mode}`} className={`${swept} ${leave} relative z-10 space-y-5 px-7 pb-7 pt-[8.25rem] md:w-[48%] md:pt-9 ${isSignup ? 'md:ml-auto' : ''}`}>
          <h1 className="text-2xl font-semibold text-fg">{isSignup ? 'Register' : 'Login'}</h1>
          {err && <div role="alert" className="flex items-start gap-2 rounded-md border-l-2 border-red-500 bg-red-500/10 px-3 py-2 text-sm text-red-300">{err}</div>}
          {msg && <div role="status" className="flex items-start gap-2 rounded-md border-l-2 border-emerald-500 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">{msg}</div>}
          <div>
            <AuthField
              icon={<User className="h-4 w-4" />}
              label="Username"
              autoComplete="username"
              required
              value={username}
              // Sanitize to the allowed handle charset during signup so invalid
              // characters can't be typed or pasted. Login is left untouched so any
              // legacy account whose username has other characters can still sign in.
              onChange={e => setUsername(isSignup ? sanitizeUsernameInput(e.target.value) : e.target.value)}
            />
            {isSignup && username && (
              <p className={`mt-1 text-meta ${usernameError ? 'text-red-300' : 'text-emerald-400'}`}>
                {usernameError || 'Looks good'}
              </p>
            )}
            {isSignup && !username && (
              <p className="mt-1 text-meta text-fg-3">3–30 characters · letters, numbers, and . _ -</p>
            )}
          </div>
          {isSignup && (
            <AuthField
              icon={<Mail className="h-4 w-4" />}
              label="Email address"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          )}
          <div>
            <AuthField
              icon={<Lock className="h-4 w-4" />}
              label="Password"
              type="password"
              autoComplete={isSignup ? AUTOFILL_HINT.signup : AUTOFILL_HINT.login}
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
            {isSignup && (
              <ul className="mt-2 space-y-0.5 text-meta">
                {[
                  { label: 'At least 8 characters', met: password.length >= 8 },
                  { label: 'One letter',  met: /[A-Z]/.test(password) },
                  { label: 'One lowercase letter',  met: /[a-z]/.test(password) },
                  { label: 'One number',            met: /[0-9]/.test(password) },
                ].map(({ label, met }) => (
                  <li key={label} className={`flex items-center gap-1.5 transition-colors duration-200 ${met ? 'text-emerald-400' : 'text-fg-3'}`}>
                    <svg viewBox="0 0 10 10" className="h-2.5 w-2.5 shrink-0" fill="currentColor">
                      {met
                        ? <path d="M5 0a5 5 0 1 1 0 10A5 5 0 0 1 5 0zm2.07 3.43L4.2 6.3 2.93 5.03a.6.6 0 1 0-.86.84l1.7 1.7a.6.6 0 0 0 .86 0l3.28-3.28a.6.6 0 1 0-.84-.86z"/>
                        : <circle cx="5" cy="5" r="3.5" stroke="currentColor" strokeWidth="1.2" fill="none"/>
                      }
                    </svg>
                    {label}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {isSignup && (
            <div>
              <AuthField
                icon={<Lock className="h-4 w-4" />}
                label="Confirm password"
                type="password"
                autoComplete={AUTOFILL_HINT.signup}
                required
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
              />
              {confirmPassword && (
                <p className={`mt-1 text-meta ${confirmPassword === password ? 'text-emerald-400' : 'text-red-300'}`}>
                  {confirmPassword === password ? 'Passwords match' : 'Passwords do not match yet'}
                </p>
              )}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-full border border-emerald-300/40 bg-gradient-to-b from-emerald-400 to-emerald-600 py-2.5 text-sm font-semibold text-black shadow-[0_0_18px_rgb(var(--accent-500)/0.35)] transition hover:brightness-110 disabled:opacity-70"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            <span>{buttonLabel}</span>
          </button>
          {mode === 'login' && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => { setMode('forgot'); setErr(''); setMsg(''); }}
                className="text-sm text-fg-2 hover:text-fg transition-colors underline-offset-2 hover:underline"
              >
                Forgot password?
              </button>
            </div>
          )}
          <div className="text-center text-sm text-fg-2">
            {mode === 'login' ? (
              <>Don&apos;t have an account? <button type="button" onClick={() => switchTo('signup')} className="font-medium text-emerald-300 underline-offset-2 hover:underline">Sign up</button></>
            ) : (
              <>Already have an account? <button type="button" onClick={() => switchTo('login')} className="font-medium text-emerald-300 underline-offset-2 hover:underline">Login</button></>
            )}
          </div>
          {isSignup && (
            <p className="text-center text-meta text-fg-3">
              By signing up you agree to our{' '}
              <a href="/terms" target="_blank" rel="noreferrer" className="underline hover:text-fg-2">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noreferrer" className="underline hover:text-fg-2">Privacy Policy</a>
            </p>
          )}
        </div>
      </form>
    </div>
  );
}
